/**
 * Risk Analytics Utilities
 * Functions for processing risk data for dashboards and visualizations
 */

/**
 * Create heat map data from risks array
 * @param {Array} risks - Array of risk objects with Severity and Likelihood
 * @param {Array} severities - Array of severity objects
 * @param {Array} likelihoods - Array of likelihood objects
 * @returns {Object} Heat map data with matrix and labels
 */
export const createRiskHeatMapData = (
  risks,
  severities = [],
  likelihoods = []
) => {
  // Initialize matrix with zeros
  const matrix = {};

  // Get severity and likelihood ranges
  const severityValues = severities.map((s) => s.value).sort((a, b) => a - b);
  const likelihoodValues = likelihoods
    .map((l) => l.value)
    .sort((a, b) => a - b);

  // Initialize all combinations to 0
  severityValues.forEach((severity) => {
    likelihoodValues.forEach((likelihood) => {
      const key = `${severity}-${likelihood}`;
      matrix[key] = 0;
    });
  });

  // Count risks in each cell
  risks.forEach((risk) => {
    const severity = risk.Severity?.value;
    const likelihood = risk.Likelihood?.value;

    if (severity && likelihood) {
      const key = `${severity}-${likelihood}`;
      matrix[key] = (matrix[key] || 0) + 1;
    }
  });

  // Create labels maps
  const severityLabels = {};
  const likelihoodLabels = {};

  severities.forEach((s) => {
    severityLabels[s.value] = s.name || s.description;
  });

  likelihoods.forEach((l) => {
    likelihoodLabels[l.value] = l.description;
  });

  return {
    matrix,
    severityValues,
    likelihoodValues,
    severityLabels,
    likelihoodLabels,
    totalRisks: risks.length,
  };
};

/**
 * Create category distribution data from risks array
 * @param {Array} risks - Array of risk objects with Category
 * @returns {Object} Category distribution data
 */
export const createRiskCategoryDistribution = (risks) => {
  const distribution = {};

  risks.forEach((risk) => {
    const category = risk.Category?.name || "Uncategorized";
    distribution[category] = (distribution[category] || 0) + 1;
  });

  // Convert to array format with percentages
  const totalRisks = risks.length;
  const distributionArray = Object.entries(distribution).map(
    ([category, count]) => ({
      category,
      count,
      percentage: totalRisks > 0 ? Math.round((count / totalRisks) * 100) : 0,
    })
  );

  // Sort by count descending
  distributionArray.sort((a, b) => b.count - a.count);

  return {
    distribution: distributionArray,
    totalRisks,
  };
};

/**
 * Get risk level color class based on risk level value
 * @param {number} riskLevel - Risk level value
 * @returns {string} CSS class for risk level color
 */
export const getRiskLevelColor = (riskLevel) => {
  if (riskLevel >= 20) return "bg-red-600 text-white"; // Critical
  if (riskLevel >= 15) return "bg-red-500 text-white"; // Very High
  if (riskLevel >= 10) return "bg-orange-500 text-white"; // High
  if (riskLevel >= 6) return "bg-yellow-500 text-black"; // Medium
  if (riskLevel >= 3) return "bg-green-400 text-black"; // Low
  return "bg-green-300 text-black"; // Very Low
};

/**
 * Get heat map cell color based on position (severity and likelihood values)
 * @param {number} severity - Severity value
 * @param {number} likelihood - Likelihood value
 * @param {Array} severities - Array of severity objects
 * @param {Array} likelihoods - Array of likelihood objects
 * @param {number} count - Number of risks in this cell (0 means no color)
 * @returns {string} CSS class for heat map cell color
 */
export const getHeatMapCellColor = (
  severity,
  likelihood,
  severities,
  likelihoods,
  count = 0
) => {
  // If there are no risks in this cell, don't color it
  if (count === 0) return "bg-gray-100 text-gray-400";

  // Get the ranges for normalization
  const severityValues = severities.map((s) => s.value).sort((a, b) => a - b);
  const likelihoodValues = likelihoods
    .map((l) => l.value)
    .sort((a, b) => a - b);

  const minSeverity = Math.min(...severityValues);
  const maxSeverity = Math.max(...severityValues);
  const minLikelihood = Math.min(...likelihoodValues);
  const maxLikelihood = Math.max(...likelihoodValues);

  // Normalize severity and likelihood to 0-1 range
  const normalizedSeverity =
    (severity - minSeverity) / (maxSeverity - minSeverity);
  const normalizedLikelihood =
    (likelihood - minLikelihood) / (maxLikelihood - minLikelihood);

  // Calculate risk level based on position (higher severity and likelihood = higher risk)
  const riskLevel = normalizedSeverity + normalizedLikelihood;

  // Map risk level to colors (0-2 range, where 2 is max risk)
  if (riskLevel >= 1.6) return "bg-red-600 text-white"; // Top right - Critical
  if (riskLevel >= 1.2) return "bg-red-500 text-white"; // High risk area
  if (riskLevel >= 0.8) return "bg-orange-500 text-white"; // Medium-high risk
  if (riskLevel >= 0.4) return "bg-yellow-500 text-black"; // Medium risk
  return "bg-green-400 text-black"; // Low risk - bottom left
};

/**
 * Get category colors for distribution chart
 * @param {number} index - Index of the category
 * @returns {string} CSS class for category color
 */
export const getCategoryColor = (index) => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-orange-500",
    "bg-purple-500",
    "bg-red-500",
    "bg-teal-500",
    "bg-pink-500",
    "bg-indigo-500",
  ];

  return colors[index % colors.length];
};

/**
 * Calculate inherent risk (total risk exposure without controls)
 * @param {Array} risks - Array of risk objects
 * @returns {number} Total inherent risk value
 */
export const calculateInherentRisk = (risks) => {
  return risks.reduce((total, risk) => {
    return total + (risk.risk_level || 0);
  }, 0);
};

/**
 * Calculate control effectiveness based on frequency and key control status
 * @param {Array} controls - Array of control objects
 * @returns {number} Control effectiveness value (0-1 scale)
 */
export const calculateControlEffectiveness = (controls) => {
  if (!controls || controls.length === 0) return 0;

  let effectiveness = 0;

  controls.forEach((control) => {
    let controlValue = 0;

    // Base effectiveness by frequency
    switch (control.frequency?.toLowerCase()) {
      case "daily":
        controlValue = 0.4;
        break;
      case "weekly":
        controlValue = 0.3;
        break;
      case "monthly":
        controlValue = 0.2;
        break;
      case "quarterly":
        controlValue = 0.15;
        break;
      case "annually":
        controlValue = 0.1;
        break;
      default:
        controlValue = 0.1;
        break;
    }

    // Key controls are 50% more effective
    if (control.is_key_control) {
      controlValue *= 1.5;
    }

    effectiveness += controlValue;
  });

  // Cap at 90% maximum effectiveness (never 100% risk elimination)
  return Math.min(effectiveness, 0.9);
};

/**
 * Calculate residual risk (risk remaining after considering controls)
 * @param {Array} risks - Array of risk objects with Controls
 * @returns {number} Total residual risk value
 */
export const calculateResidualRisk = (risks) => {
  return risks.reduce((total, risk) => {
    const inherentRiskLevel = risk.risk_level || 0;

    if (!risk.Controls || risk.Controls.length === 0) {
      // No controls = full inherent risk remains
      return total + inherentRiskLevel;
    }

    // Calculate control effectiveness
    const controlEffectiveness = calculateControlEffectiveness(risk.Controls);

    // Residual = Inherent × (1 - effectiveness)
    const residualRiskLevel = inherentRiskLevel * (1 - controlEffectiveness);

    return total + residualRiskLevel;
  }, 0);
};

/**
 * Calculate risk coverage percentage using the formula: (inherent risk - residual risk) / inherent risk
 * @param {Array} risks - Array of risk objects with Controls
 * @returns {number} Risk coverage percentage (0-100)
 */
export const calculateRiskCoverage = (risks) => {
  if (risks.length === 0) return 0;

  const inherentRisk = calculateInherentRisk(risks);
  const residualRisk = calculateResidualRisk(risks);

  // Avoid division by zero
  if (inherentRisk === 0) return 0;

  // Calculate coverage using the formula: (inherent risk - residual risk) / inherent risk
  const coverage = (inherentRisk - residualRisk) / inherentRisk;

  // Convert to percentage and round
  return Math.round(coverage * 100);
};

/**
 * Get organization-specific risk scale information
 * @param {Array} severities - Array of severity objects
 * @param {Array} likelihoods - Array of likelihood objects
 * @returns {Object} Risk scale information
 */
export const getOrganizationRiskScale = (severities, likelihoods) => {
  if (!severities.length || !likelihoods.length) {
    return {
      severityRange: { min: 1, max: 5 },
      likelihoodRange: { min: 1, max: 5 },
      maxPossibleRisk: 25,
      minPossibleRisk: 1,
    };
  }

  const severityRange = {
    min: Math.min(...severities.map((s) => s.value)),
    max: Math.max(...severities.map((s) => s.value)),
  };

  const likelihoodRange = {
    min: Math.min(...likelihoods.map((l) => l.value)),
    max: Math.max(...likelihoods.map((l) => l.value)),
  };

  return {
    severityRange,
    likelihoodRange,
    maxPossibleRisk: severityRange.max * likelihoodRange.max,
    minPossibleRisk: severityRange.min * likelihoodRange.min,
  };
};

/**
 * Calculate detailed coverage metrics using the formula: (inherent risk - residual risk) / inherent risk
 * @param {Array} risks - Array of risk objects with Controls
 * @returns {Object} Detailed coverage metrics
 */
export const calculateCoverageMetrics = (risks) => {
  const totalRisks = risks.length;
  if (totalRisks === 0)
    return { coverage: 0, covered: 0, uncovered: 0, totalRisks: 0 };

  const inherentRisk = calculateInherentRisk(risks);
  const residualRisk = calculateResidualRisk(risks);

  // Avoid division by zero
  if (inherentRisk === 0) {
    return { coverage: 0, covered: 0, uncovered: 0, totalRisks: 0 };
  }

  // Calculate coverage using the formula: (inherent risk - residual risk) / inherent risk
  const coverage = (inherentRisk - residualRisk) / inherentRisk;

  // Count risks with and without controls for additional metrics
  const coveredRisks = risks.filter(
    (risk) => risk.Controls && risk.Controls.length > 0
  );

  const uncoveredRisks = risks.filter(
    (risk) => !risk.Controls || risk.Controls.length === 0
  );

  return {
    coverage: Math.round(coverage * 100),
    covered: coveredRisks.length,
    uncovered: uncoveredRisks.length,
    totalRisks,
  };
};

/**
 * Format risk value for display
 * @param {number} value - Risk value
 * @param {number} maxValue - Maximum possible risk value (for context)
 * @returns {string} Formatted display value
 */
export const formatRiskValue = (value, maxValue = null) => {
  if (value === 0) return "0";

  // Round to 1 decimal place if decimal, otherwise show as integer
  const rounded = Math.round(value * 10) / 10;
  return rounded % 1 === 0 ? rounded.toString() : rounded.toFixed(1);
};
